import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSessionTimelineTheme } from '../theme';
import { formatEventTimestamp, formatElapsedTime, formatWaitingTime } from '../lib/formatTimestamp';
import { TokenizedValue } from './TokenizedValue';
import { hasTokens } from '@/shared/lib/sessionTokens/parseTokens';
import type { ExpandState } from '../types/sessionTimeline.types';

interface EventCardHeaderProps {
  sessionIcon?: React.ReactNode;
  title: string;
  index: number;
  occurredAt: Date | string;
  expandState: ExpandState;
  collapsible?: boolean;
  onToggleExpand: () => void;
  previousOccurredAt?: Date | string; // 이전 세션의 occurredAt (경과 시간 계산용)
  isLatestSession?: boolean; // 가장 최신 세션인지 여부
}

export function EventCardHeader({
  sessionIcon,
  title,
  index,
  occurredAt,
  expandState,
  collapsible = true,
  onToggleExpand,
  previousOccurredAt,
  isLatestSession = false,
}: EventCardHeaderProps) {
  const { getHeaderSx, getTitleSx, getIndexBadgeSx, getTimestampSx, getExpandButtonSx, tokens } =
    useSessionTimelineTheme();

  // 최신 세션의 경우 실시간으로 현재 시간 업데이트 (세션 대기시간 표시용)
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    if (!isLatestSession) return;

    // 최신 세션인 경우에만 1초마다 업데이트
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [isLatestSession]);

  const formattedTimestamp = formatEventTimestamp(occurredAt);
  // 이전 세션이 있으면 이전 세션 occurredAt부터 현재 세션 occurredAt까지의 경과 시간 계산
  const elapsedTime = previousOccurredAt ? formatElapsedTime(previousOccurredAt, occurredAt) : '';
  // 최신 세션인 경우 현재 세션 occurredAt부터 현재 시간까지의 대기시간 계산
  const waitingTime = isLatestSession ? formatWaitingTime(occurredAt, currentTime) : '';
  const isExpanded = expandState !== 'collapsed';

  return (
    <Box>
      <Box sx={getHeaderSx()}>
        <Box sx={getTitleSx()}>
          {sessionIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: tokens.header.icon.color,
                fontSize: tokens.header.icon.size,
                flexShrink: 0,
              }}
            >
              {sessionIcon}
            </Box>
          )}
          <Typography component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {hasTokens(title) ? <TokenizedValue value={title} /> : title}
          </Typography>
          <Box component="span" sx={getIndexBadgeSx()}>
            #{index}
          </Box>
        </Box>
        {collapsible && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            sx={{
              ...getExpandButtonSx(),
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
            aria-label={isExpanded ? '접기' : '펼치기'}
            aria-expanded={isExpanded}
          >
            <ExpandMoreIcon sx={{ fontSize: tokens.header.expandButton.size }} />
          </IconButton>
        )}
      </Box>
      <Typography sx={getTimestampSx()}>
        {formattedTimestamp}
        {elapsedTime && ` / ${elapsedTime}`}
        {waitingTime && ` / ${waitingTime}`}
      </Typography>
    </Box>
  );
}
